import * as React from "react";
import { useInterval } from "react-use";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { useTokenMaker } from "../../../../hooks";
import { JobSummary, isValidJobSummary } from "./ExecutionMatrix";
import { RenderJobHistoryPresenter } from "./RenderJobHistoryPresenter";

// Render API response type
type RenderJobFromAPI = {
  id: string;
  serviceId: string;
  startCommand: string;
  planId: string;
  status: string; // 'pending', 'running', 'succeeded', 'failed'
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  exitCode?: number;
};

export type RenderJob = {
  id: string;
  startCommand: string;
  status: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  exitCode: number | null;
  dashboard_url: string;
  duration_seconds: number | null;
};

export const RenderJobHistory: React.FC<{}> = () => {
  const { url: webURL } = useTokenMaker();
  const snackbar = useSnackbar();
  const [jobs, setJobs] = React.useState<RenderJob[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [jobSummaries, setJobSummaries] = React.useState<Map<string, JobSummary>>(new Map());
  const [isForceRunning, setIsForceRunning] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Track already-fetched job IDs to prevent re-fetching on every jobs change
  const fetchedJobIdsRef = React.useRef<Set<string>>(new Set());

  const fetchJobs = React.useCallback(async () => {
    // Set refreshing state (but only if we have jobs already)
    const hasJobs = jobs.length > 0;
    if (hasJobs) {
      setIsRefreshing(true);
    }

    try {
      const response = await fetch(`${webURL}/jobs/render/list`);
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform Render API response to our format
      // Render API returns array of {cursor, job} objects
      // Filter out jobs without IDs (API sometimes returns incomplete data)
      const mapped: RenderJob[] = data
        .filter((item: any) => item.job && item.job.id)
        .map((item: any) => {
          const job = item.job as RenderJobFromAPI;
          return {
            id: job.id,
            startCommand: job.startCommand,
            status: job.status,
            createdAt: job.createdAt,
            startedAt: job.startedAt || null,
            finishedAt: job.finishedAt || null,
            exitCode: job.exitCode ?? null,
            dashboard_url: `https://dashboard.render.com/jobs/${job.id}`,
            duration_seconds:
              job.startedAt && job.finishedAt
                ? (new Date(job.finishedAt).getTime() -
                    new Date(job.startedAt).getTime()) /
                  1000
                : null,
          };
        });

      setJobs(mapped);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch Render jobs:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [webURL, jobs.length]);

  React.useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Fetch job summaries (execution details from database)
  // Only fetches summaries for NEW jobs (not already fetched) to avoid race conditions
  const fetchJobSummaries = React.useCallback(async (jobs: RenderJob[]) => {
    const completedJobs = jobs.filter(
      j =>
        (j.status === 'succeeded' || j.status === 'failed') &&
        !fetchedJobIdsRef.current.has(j.id)  // ONLY fetch NEW jobs
    );

    if (completedJobs.length === 0) return;  // No new jobs to fetch

    const summaryPromises = completedJobs.map(async (job) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      try {
        const response = await fetch(
          `${webURL}/jobs/render/${job.id}/summary`,
          { signal: controller.signal }
        );
        const data = await response.json();

        // Validate data structure at runtime
        if (!isValidJobSummary(data)) {
          console.warn(`Invalid summary data for ${job.id}`, data);
          return { jobId: job.id, summary: null, error: 'Invalid data format' };
        }

        fetchedJobIdsRef.current.add(job.id);  // Mark as fetched
        return { jobId: job.id, summary: data, error: null };
      } catch (err: any) {
        if (err.name === 'AbortError') {
          console.warn(`Summary fetch timeout for ${job.id}`);
        } else {
          console.error(`Failed to fetch summary for ${job.id}:`, err);
        }
        return { jobId: job.id, summary: null, error: err.message };
      } finally {
        clearTimeout(timeoutId);
      }
    });

    const results = await Promise.all(summaryPromises);
    setJobSummaries(prev => {
      const updated = new Map(prev);
      results.forEach(({ jobId, summary }) => {
        if (summary) updated.set(jobId, summary);
      });
      return updated;
    });
  }, [webURL]);

  React.useEffect(() => {
    if (jobs.length > 0) {
      fetchJobSummaries(jobs);
    }
  }, [jobs, fetchJobSummaries]);

  // Force run handler
  const handleForceRun = React.useCallback(async () => {
    setIsForceRunning(true);
    try {
      const response = await fetch(`${webURL}/jobs/force-update-aircraft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to force run job");
      }

      snackbar.notify({
        text: "Force run triggered - aircraft update will run regardless of checksum",
        severity: "success",
      });

      // Refresh job list immediately
      setTimeout(() => fetchJobs(), 1000);
    } catch (err) {
      console.error("Force run failed:", err);
      snackbar.notify({
        text: `Failed to force run: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
        severity: "error",
      });
    } finally {
      setIsForceRunning(false);
    }
  }, [webURL, fetchJobs, snackbar]);

  // Auto-refresh every 10 seconds (matches FlightBridgeStatus pattern)
  useInterval(fetchJobs, 10 * 1000);

  return (
    <RenderJobHistoryPresenter
      jobs={jobs}
      loading={loading}
      error={error}
      jobSummaries={jobSummaries}
      onForceRun={handleForceRun}
      isForceRunning={isForceRunning}
      isRefreshing={isRefreshing}
    />
  );
};
